import { cn } from '@/lib/utils';

interface CategoryHeroProps {
  name: string;
  homeBaseName: string;
  imageUrl?: string;
  totalCardCount: number;
  subCategoryCount: number;
  friendCount: number;
  className?: string;
}

/**
 * Photo hero atop the Category Detail page. When `imageUrl` is set, the photo
 * fills the card as a background image (carried in a `role="img"` div with the
 * parent name as its accessible label so it isn't duplicated by the `<h1>`).
 * Without an image, a brand-tinted gradient fills the same shape.
 *
 * Layout follows the spec's `<CategoryHero>` contract: dark scrim, bottom row
 * with breadcrumb eyebrow + name + summary on the left, glassy total pill on
 * the right.
 */
export function CategoryHero({
  name,
  homeBaseName,
  imageUrl,
  totalCardCount,
  subCategoryCount,
  friendCount,
  className,
}: CategoryHeroProps): React.JSX.Element {
  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-[36px] shadow-lg',
        'h-[220px] md:h-[260px]',
        className,
      )}
    >
      {imageUrl ? (
        <div
          role="img"
          aria-label={name}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
      ) : (
        <div
          data-testid="hero-gradient-fallback"
          className="absolute inset-0 bg-gradient-to-br from-[var(--stash-blue-light)] to-muted"
        />
      )}

      {/* Dark scrim for legibility. */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/15 to-black/70" aria-hidden="true" />

      {/* Bottom content row. */}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6 md:p-7">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-white/80">
            {homeBaseName} › Category
          </p>
          <h1 className="mt-1 truncate text-4xl font-bold tracking-tight text-white md:text-[46px]">
            {name}
          </h1>
          <p className="mt-1 text-sm font-medium text-white/85">
            {subCategoryCount} sub-categories
            {friendCount > 0 && <> · stashed by you &amp; {friendCount} friends</>}
          </p>
        </div>

        {/* Glassy total pill. */}
        <div className="shrink-0 rounded-2xl border border-white/24 bg-white/16 px-4 py-2 text-center text-white backdrop-blur">
          <div className="text-[30px] font-bold leading-none">{totalCardCount}</div>
          <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider">Cards</div>
        </div>
      </div>
    </section>
  );
}
